// =============================================================================
// SERIALIZATION UTILITIES
// =============================================================================
// Utilities for converting content data to/from JSON-safe formats

import type { ContentComponent, NavItem, SEOData } from '@/types';

// JSON-safe types (all Date objects converted to strings)
export interface SerializedComponent {
  id: string;
  type: string;
  title: string;
  content: string;
  order?: number;
  fullWidth?: boolean;
  metadata?: Record<string, unknown>;
  createdAt: string;  // ISO 8601 string
  updatedAt: string;  // ISO 8601 string
}

export interface PageData {
  pageId: string;
  version: string;
  lastModified: string;
  seo?: SEOData;
  navigation?: {
    navItems?: NavItem[];
    footerLinks?: NavItem[];
    socialLinks?: NavItem[];
  };
  components: ContentComponent[];
}

export interface SerializedPageData {
  pageId: string;
  version: string;
  lastModified: string;
  seo?: SEOData;
  navigation?: {
    navItems?: NavItem[];
    footerLinks?: NavItem[];
    socialLinks?: NavItem[];
  };
  components: SerializedComponent[];
}

/**
 * Convert a ContentComponent to a JSON-safe serialized format
 * Recursively serializes nested components in metadata.pages (for storybook-hero)
 */
export function serializeComponent(component: ContentComponent): SerializedComponent {
  // Serialize metadata, handling nested components in pages array
  let metadata = component.metadata;
  
  // If this is a storybook-hero component, serialize nested pages first
  if (component.type === 'storybook-hero' && metadata?.pages && Array.isArray(metadata.pages)) {
    const pages = metadata.pages.map((page: ContentComponent) => serializeComponent(page));
    // Create new metadata object with serialized pages, then sanitize the rest
    const { pages: _, ...restMetadata } = metadata;
    const sanitizedRest = sanitizeMetadata(restMetadata as Record<string, unknown>);
    metadata = {
      ...sanitizedRest,
      pages,
    } as typeof metadata;
  } else if (component.type === 'two-column' && metadata) {
    // Handle two-column component's nested leftComponent and rightComponent
    const { leftComponent, rightComponent, ...restMetadata } = metadata;
    const sanitizedRest = sanitizeMetadata(restMetadata as Record<string, unknown>);
    metadata = {
      ...sanitizedRest,
      leftComponent: leftComponent ? serializeComponent(leftComponent as ContentComponent) : undefined,
      rightComponent: rightComponent ? serializeComponent(rightComponent as ContentComponent) : undefined,
    } as typeof metadata;
  } else if (metadata) {
    metadata = sanitizeMetadata(metadata as Record<string, unknown>) as typeof metadata;
  }
  
  return {
    id: component.id,
    type: component.type,
    title: component.title,
    content: component.content,
    order: component.order,
    fullWidth: component.fullWidth,
    metadata,
    createdAt: component.createdAt instanceof Date 
      ? component.createdAt.toISOString() 
      : component.createdAt as string,
    updatedAt: component.updatedAt instanceof Date 
      ? component.updatedAt.toISOString() 
      : component.updatedAt as string,
  };
}

/**
 * Convert a serialized component back to ContentComponent with Date objects
 * Recursively deserializes nested components in metadata.pages (for storybook-hero)
 */
export function deserializeComponent(serialized: SerializedComponent): ContentComponent {
  // Deserialize metadata, handling nested components in pages array
  let metadata = serialized.metadata;
  
  // If this is a storybook-hero component, deserialize nested pages
  if (serialized.type === 'storybook-hero' && metadata?.pages && Array.isArray(metadata.pages)) {
    metadata = {
      ...metadata,
      pages: (metadata.pages as unknown[]).map((page: unknown) => {
        // Check if it's a serialized component
        if (page && typeof page === 'object' && 'id' in page && 'type' in page) {
          return deserializeComponent(page as SerializedComponent);
        }
        return page;
      }),
    };
  } else if (serialized.type === 'two-column' && metadata) {
    // Handle two-column component's nested leftComponent and rightComponent
    const { leftComponent, rightComponent, ...restMetadata } = metadata;
    metadata = {
      ...restMetadata,
      leftComponent: leftComponent && typeof leftComponent === 'object' && 'id' in leftComponent && 'type' in leftComponent
        ? deserializeComponent(leftComponent as SerializedComponent)
        : leftComponent,
      rightComponent: rightComponent && typeof rightComponent === 'object' && 'id' in rightComponent && 'type' in rightComponent
        ? deserializeComponent(rightComponent as SerializedComponent)
        : rightComponent,
    };
  }
  
  return {
    id: serialized.id,
    type: serialized.type as ContentComponent['type'],
    title: serialized.title,
    content: serialized.content,
    order: serialized.order,
    fullWidth: serialized.fullWidth,
    metadata,
    createdAt: new Date(serialized.createdAt),
    updatedAt: new Date(serialized.updatedAt),
  };
}

/**
 * Serialize an entire page data object
 */
export function serializePageData(pageData: PageData): SerializedPageData {
  return {
    pageId: pageData.pageId,
    version: pageData.version,
    lastModified: pageData.lastModified,
    seo: pageData.seo,
    navigation: pageData.navigation,
    components: pageData.components.map(serializeComponent),
  };
}

/**
 * Deserialize an entire page data object
 */
export function deserializePageData(serialized: SerializedPageData): PageData {
  return {
    pageId: serialized.pageId,
    version: serialized.version,
    lastModified: serialized.lastModified,
    seo: serialized.seo,
    navigation: serialized.navigation,
    components: serialized.components.map(deserializeComponent),
  };
}

/**
 * Convert page data to JSON string
 */
export function toJSON(pageData: PageData): string {
  const serialized = serializePageData(pageData);
  return JSON.stringify(serialized, null, 2);
}

/**
 * Parse JSON string to page data
 */
export function fromJSON(json: string): PageData {
  const parsed = JSON.parse(json) as SerializedPageData;
  return deserializePageData(parsed);
}

/**
 * Sanitize metadata to ensure it's JSON-safe
 * Removes any functions, undefined values, and converts Dates to strings
 */
function sanitizeMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(metadata)) {
    // Skip undefined and functions
    if (value === undefined || typeof value === 'function') {
      continue;
    }
    
    // Convert Date objects to ISO strings
    if (value instanceof Date) {
      sanitized[key] = value.toISOString();
      continue;
    }
    
    // Recursively sanitize nested objects
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeMetadata(value as Record<string, unknown>);
      continue;
    }
    
    // Recursively sanitize arrays
    if (Array.isArray(value)) {
      sanitized[key] = value.map(item => {
        if (item instanceof Date) return item.toISOString();
        if (item !== null && typeof item === 'object') {
          return sanitizeMetadata(item as Record<string, unknown>);
        }
        return item;
      });
      continue;
    }
    
    // Primitive values are fine
    sanitized[key] = value;
  }
  
  return sanitized;
}

/**
 * Validate a serialized component has required fields
 */
export function validateSerializedComponent(component: unknown): component is SerializedComponent {
  if (!component || typeof component !== 'object') return false;
  
  const c = component as Partial<SerializedComponent>;
  
  return !!(
    c.id &&
    c.type &&
    c.title !== undefined &&
    c.content !== undefined &&
    c.createdAt &&
    c.updatedAt &&
    typeof c.id === 'string' &&
    typeof c.type === 'string' &&
    typeof c.title === 'string' &&
    typeof c.content === 'string' &&
    typeof c.createdAt === 'string' &&
    typeof c.updatedAt === 'string'
  );
}

/**
 * Validate entire page data structure
 */
export function validatePageData(data: unknown): data is SerializedPageData {
  if (!data || typeof data !== 'object') return false;
  
  const d = data as Partial<SerializedPageData>;
  
  if (!d.pageId || !d.version || !d.components) return false;
  if (!Array.isArray(d.components)) return false;
  
  return d.components.every(validateSerializedComponent);
}

/**
 * Create a new empty page data structure
 */
export function createEmptyPageData(pageId: string): PageData {
  return {
    pageId,
    version: '1.0.0',
    lastModified: new Date().toISOString(),
    components: [],
  };
}

/**
 * Add order property to components if missing
 */
export function addComponentOrder(components: ContentComponent[]): ContentComponent[] {
  return components.map((component, index) => ({
    ...component,
    order: component.order !== undefined ? component.order : index,
  }));
}

/**
 * Sort components by order property
 */
export function sortComponentsByOrder(components: ContentComponent[]): ContentComponent[] {
  return [...components].sort((a, b) => {
    const orderA = a.order !== undefined ? a.order : 999;
    const orderB = b.order !== undefined ? b.order : 999;
    return orderA - orderB;
  });
}

